import React, { useState, useEffect } from 'react';
import { IoMdArrowRoundBack } from 'react-icons/io';
import {
  FaChevronLeft,
  FaChevronRight,
  FaTimes,
  FaBed,
  FaUsers,
  FaStar,
  FaCheck,
  FaArrowUp,
} from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Navigation, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

const RoomUpgrade = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [upgradeOptions, setUpgradeOptions] = useState([]);
  const [currentRoomDetails, setCurrentRoomDetails] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [showRoomImagesModal, setShowRoomImagesModal] = useState(false);
  const [currentRoomImages, setCurrentRoomImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [policyInfo, setPolicyInfo] = useState(null);

  // Get booking details from localStorage
  const bookingData = JSON.parse(localStorage.getItem('bookingDetails')) || {};
  const primaryBooking = bookingData.primaryBooking || {};

  // Get hotel property details from localStorage
  const hotelPropertyCache = JSON.parse(localStorage.getItem('hotelPropertyCache') || '{}');
  const hotelEmail = hotelPropertyCache?.data?.email || 'support@hotel.com';
  const phone = hotelPropertyCache?.data?.phone || '+1 234-567-890';

  // Extract booking details
  const bookingId = primaryBooking?.booking_id || '';
  const orderId = primaryBooking?.order_id || '';
  const currentRoomType = primaryBooking?.room_type?.name || '';
  const guestName = primaryBooking?.guest_name || '';
  const checkinDate = primaryBooking?.checkin_date || '';
  const checkoutDate = primaryBooking?.checkout_date || '';
  const lengthOfStay = primaryBooking?.length_of_stay || 0;

  // Format currency function
  const formatCurrency = (amount) => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(numAmount)) return '₹0.00';

    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numAmount);
  };

  // Format date for display
  const formatDateToDisplay = (dateString) => {
    if (!dateString) return '';

    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Fetch upgrade options from the new API
  useEffect(() => {
    if (!bookingId) {
      setApiError('Booking ID not found. Please go back and try again.');
      return;
    }

    setLoading(true);
    fetch(`${BQ_BASE_URL}/bq/api/modify/${bookingId}/options`, {
      method: 'GET',
      headers: {
        accept: 'application/json',
      },
    })
      .then(async (response) => {
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.message || errorData.detail || 'Failed to fetch upgrade options'
          );
        }
        return response.json();
      })
      .then((data) => {
        if (data.status === 'success') {
          console.log('Upgrade options API response:', data);

          setCurrentRoomDetails(data.data.current_room_type_details);
          setPolicyInfo(data.data.policy);

          const upgradeOnly = data.data.available_modifications
            .filter((option) => option.modification_type === 'upgrade' && option.available === true)
            .map((option) => ({
              roomtypename: option.roomtypename,
              description: option.description,
              baseprice: option.baseprice,
              max_occupancy: option.max_occupancy,
              images: option.images || [],
              amenities: option.amenities || [],
              service_categories: option.service_categories || [],
              total_nights: option.total_nights || lengthOfStay,
              price_difference: option.price_difference || 0,
              final_price_difference: option.final_price_difference || 0,
              preview_total: option.preview_total || 0,
              estimated_total: option.estimated_total || 0,
              price_details: option.price_details || {},
              price_breakdown: option.price_breakdown || [],
              price_frozen: option.price_frozen || false,
              available: option.available,
              is_after_checkin: option.is_after_checkin,
              is_within_24_hours: option.is_within_24_hours,
              freeze_reason: option.freeze_reason,
              freeze_message: option.freeze_message,
            }));

          console.log('Filtered upgrade options:', upgradeOnly);
          setUpgradeOptions(upgradeOnly);
          setApiError(null);
        } else {
          if (data.message && data.message.includes('No upgrade options')) {
            setUpgradeOptions([]);
          } else {
            throw new Error(data.message || 'Failed to fetch upgrade options');
          }
        }
      })
      .catch((error) => {
        console.error('Error fetching upgrade options:', error);
        setApiError(error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [bookingId, lengthOfStay]);

  // Open modal and fetch preview data
  const openModal = async (room) => {
    setSelectedRoom(room);
    setLoadingPreview(true);
    setIsModalOpen(true);

    try {
      // Send the new room details in the request body
      const response = await fetch(`${BQ_BASE_URL}/bq/api/modify/${bookingId}/preview`, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          new_room_type_name: room.roomtypename,
          new_checkin_date: checkinDate,
          new_checkout_date: checkoutDate,
          amenities: [],
          room_services: [],
          food_orders: [],
        }),
      });

      const data = await response.json();

      if (response.ok && data.status === 'success') {
        setPreviewData(data.data);
        console.log('Preview data:', data.data);
      } else {
        const errorMessage = data.message || data.detail || 'Failed to fetch preview details';
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Error fetching preview details:', error);
      toast.error(error.message || 'Failed to fetch upgrade details', {
        position: 'top-right',
      });
    } finally {
      setLoadingPreview(false);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedRoom(null);
    setPreviewData(null);
    setShowBreakdown(false);
  };

  const handleUpgradeConfirm = async () => {
    if (!selectedRoom || !bookingId) return;

    // 1. Update localStorage immediately
    const bookingDetails = JSON.parse(localStorage.getItem('bookingDetails') || '{}');
    if (bookingDetails.primaryBooking) {
      bookingDetails.primaryBooking.room_type.name = selectedRoom.roomtypename;
      localStorage.setItem('bookingDetails', JSON.stringify(bookingDetails));
    }

    // 2. Call the API with query parameters (not JSON body)
    try {
      // Build URL with query parameters
      const url = new URL(`${BQ_BASE_URL}/bq/api/modify/${bookingId}/modify`);
      url.searchParams.append('new_room_type_name', selectedRoom.roomtypename);
      url.searchParams.append('new_checkin_date', checkinDate);
      url.searchParams.append('new_checkout_date', checkoutDate);

      const response = await fetch(url.toString(), {
        method: 'POST',
        headers: {
          accept: 'application/json',
          // Remove Content-Type header since we're not sending JSON body
        },
        // Remove the body since we're using query parameters
      });

      const data = await response.json();
      console.log('API Response:', data);

      // Check if room actually changed
      if (data.status === 'success') {
        console.log('Room successfully changed!');
        toast.success(`Room upgraded to ${selectedRoom.roomtypename}!`);

        setTimeout(() => {
          window.location.href = '/reservation/options/booking/welcome';
        }, 1000);
      } else {
        throw new Error(data.message || 'Failed to upgrade room');
      }
    } catch (error) {
      console.error('API Error:', error);
      toast.error(error.message || 'Failed to upgrade room', {
        position: 'top-right',
      });
    }
  };

  // Image gallery functions
  const openRoomImagesModal = (images) => {
    if (images && images.length > 0) {
      setCurrentRoomImages(images);
      setCurrentImageIndex(0);
      setShowRoomImagesModal(true);
    }
  };

  const closeRoomImagesModal = () => {
    setShowRoomImagesModal(false);
    setCurrentRoomImages([]);
    setCurrentImageIndex(0);
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === currentRoomImages.length - 1 ? 0 : prevIndex + 1
    );
  };

  const handlePrevImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? currentRoomImages.length - 1 : prevIndex - 1
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 py-8 px-4">
      <ToastContainer />

      <div className="max-w-7xl mx-auto">
        {/* Header with back button */}
        <div className="mb-5 relative">
          <Link
            to="/reservation/options/booking/welcome"
            className="inline-flex items-center text-gray-700 hover:text-gray-900 transition font-medium mb-4"
          >
            <IoMdArrowRoundBack size={20} className="mr-2" />
            Back
          </Link>

          <div className="text-center">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">
              Exclusive Room Upgrades
            </h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Enhance your stay with our premium room selections to find the perfect upgrade.
            </p>
          </div>
        </div>

        {/* Error Message Display */}
        {apiError && (
          <div className="mb-8 p-6 bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                <FaTimes className="text-red-600 text-2xl" />
              </div>
              <h2 className="text-xl font-bold text-gray-800 mb-2">
                Upgrade Options Not Available
              </h2>
              <p className="text-gray-600 mb-6 max-w-md mx-auto">{apiError}</p>
              <Link
                to="/reservation/options/booking/welcome"
                className="inline-flex items-center px-6 py-3 bg-gray-900 text-white font-medium rounded-lg hover:bg-gray-800 transition-colors duration-200"
              >
                Return to Dashboard
              </Link>
            </div>
          </div>
        )}

        {!apiError && (
          <>
            {/* Current Room Info */}
            {currentRoomDetails && (
              <div className="mb-5 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-4 md:p-6">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-gray-100 rounded-lg">
                          <FaBed className="text-gray-700 text-xl" />
                        </div>
                        <div>
                          <span className="text-sm text-gray-500 font-medium">
                            CURRENT ROOM DETAILS
                          </span>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-2">
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <p className="text-sm text-gray-600 mb-1">CURRENT ROOM</p>
                          <p className="font-medium text-gray-800">
                            {currentRoomDetails.roomtypename}
                          </p>
                        </div>
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <p className="text-sm text-gray-600 mb-1">Stay Duration</p>
                          <p className="font-medium text-gray-800">{lengthOfStay} nights</p>
                        </div>
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <p className="text-sm text-gray-600 mb-1">Check-in</p>
                          <p className="font-medium text-gray-800">
                            {formatDateToDisplay(checkinDate)}
                          </p>
                        </div>
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <p className="text-sm text-gray-600 mb-1">Check-out</p>
                          <p className="font-medium text-gray-800">
                            {formatDateToDisplay(checkoutDate)}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="md:w-56">
                      <div className="p-4 bg-gray-900 text-white rounded-lg">
                        <p className="text-sm text-gray-300 mb-1">Current Rate</p>
                        <p className="text-2xl font-bold">
                          {formatCurrency(currentRoomDetails.baseprice)}
                          <span className="text-sm">/per night</span>
                        </p>
                        <p className="text-sm text-gray-300 mt-3">
                          Total for {lengthOfStay} nights
                        </p>
                        <p className="text-lg font-bold">
                          {formatCurrency(currentRoomDetails.baseprice * lengthOfStay)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Upgrade Options */}
            {loading ? (
              <div className="flex justify-center items-center py-20">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading upgrade options...</p>
                </div>
              </div>
            ) : upgradeOptions.length > 0 ? (
              <>
                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                  {upgradeOptions.map((room, index) => (
                    <div
                      key={index}
                      className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-all duration-300 group"
                    >
                      {/* Image Gallery */}
                      <div className="relative h-64 overflow-hidden">
                        {room.images?.length > 0 ? (
                          <>
                            <Swiper
                              pagination={{
                                type: 'fraction',
                                dynamicBullets: true,
                              }}
                              navigation={{
                                nextEl: '.swiper-button-next',
                                prevEl: '.swiper-button-prev',
                              }}
                              autoplay={{
                                delay: 3000,
                                disableOnInteraction: false,
                              }}
                              modules={[Pagination, Navigation, Autoplay]}
                              className="h-full w-full"
                            >
                              {room.images.map((image, imgIndex) => (
                                <SwiperSlide key={imgIndex}>
                                  <img
                                    src={image}
                                    alt={room.roomtypename}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                    loading="lazy"
                                  />
                                </SwiperSlide>
                              ))}
                              <div className="swiper-button-next text-white bg-black bg-opacity-40 p-2 rounded-full hover:bg-opacity-60 transition"></div>
                              <div className="swiper-button-prev text-white bg-black bg-opacity-40 p-2 rounded-full hover:bg-opacity-60 transition"></div>
                            </Swiper>
                            <button
                              onClick={() => openRoomImagesModal(room.images)}
                              className="absolute top-3 right-3 bg-black bg-opacity-60 text-white p-2 rounded-full hover:bg-opacity-80 transition z-10"
                              aria-label="View all images"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-5 w-5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                                />
                              </svg>
                            </button>
                          </>
                        ) : (
                          <div className="w-full h-full bg-gradient-to-r from-gray-100 to-gray-200 flex items-center justify-center">
                            <FaBed className="text-gray-400 text-4xl" />
                          </div>
                        )}

                        {/* Upgrade Badge */}
                        <div className="absolute top-3 left-3">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-900 text-white">
                            <FaArrowUp className="mr-1" size={10} />
                            UPGRADE
                          </span>
                        </div>

                        {/* Price Increase Badge */}
                        {room.price_difference > 0 && (
                          <div className="absolute top-3 right-3">
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                              +{formatCurrency(room.price_difference)}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Room Content */}
                      <div className="p-5">
                        <div className="mb-4">
                          <h3 className="text-xl font-bold text-gray-900 mb-2">
                            {room.roomtypename}
                          </h3>
                          <p className="text-gray-600 text-sm line-clamp-2 mb-3">
                            {room.description ||
                              'Premium accommodation with enhanced amenities and services'}
                          </p>

                          <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                            {room.max_occupancy && (
                              <span className="flex items-center">
                                <FaUsers className="mr-1.5" />
                                Up to {room.max_occupancy} guests
                              </span>
                            )}
                            <span className="flex items-center">
                              <FaBed className="mr-1.5" />
                              {room.total_nights} nights
                            </span>
                          </div>
                        </div>

                        {/* Amenities Preview */}
                        {room.amenities?.length > 0 && (
                          <div className="mb-4">
                            <p className="text-sm font-medium text-gray-700 mb-2">Key Amenities</p>
                            <div className="flex flex-wrap gap-2">
                              {room.amenities.slice(0, 3).map((amenity, idx) => (
                                <span
                                  key={idx}
                                  className="inline-flex items-center text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded"
                                >
                                  <FaCheck className="mr-1" size={8} />
                                  {amenity.name}
                                </span>
                              ))}
                              {room.amenities.length > 3 && (
                                <span className="text-xs text-gray-500">
                                  +{room.amenities.length - 3} more
                                </span>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Pricing */}
                        <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-gray-700 font-medium">Additional Cost:</span>
                            <span className="text-lg font-bold text-gray-900">
                              {formatCurrency(room.final_price_difference || 0)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center text-sm mb-1">
                            <span className="text-gray-600">New total for stay:</span>
                            <span className="font-medium text-gray-800">
                              {formatCurrency(room.estimated_total || 0)}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 mt-2">
                            Includes taxes, fees, and service charges
                          </div>
                        </div>

                        {/* Upgrade Button */}
                        <button
                          onClick={() => openModal(room)}
                          disabled={room.price_frozen || !room.available}
                          className={`w-full py-3 px-4 font-medium rounded-lg transition-all duration-200 flex items-center justify-center gap-2 ${
                            room.price_frozen || !room.available
                              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                              : 'bg-gray-900 text-white hover:bg-gray-800'
                          }`}
                        >
                          {room.price_frozen ? 'Upgrade Not Available' : 'View Upgrade Details'}
                          {!room.price_frozen && room.available && (
                            <FaArrowUp className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              !loading && (
                <div className="text-center py-16 bg-white rounded-xl shadow-sm border border-gray-200">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                    <FaStar className="text-gray-600 text-2xl" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-2">
                    No Upgrade Options Available
                  </h3>
                  <p className="text-gray-600 max-w-md mx-auto mb-6">
                    All premium rooms matching your criteria are currently unavailable. You can try
                    adjusting your dates or contact reception for assistance.
                  </p>
                  <Link
                    to="/reservation/options/booking/welcome"
                    className="inline-flex items-center px-6 py-3 bg-gray-900 text-white font-medium rounded-lg hover:bg-gray-800 transition-colors"
                  >
                    Return to Dashboard
                  </Link>
                </div>
              )
            )}
          </>
        )}

        {/* Preview Modal */}
        {isModalOpen && (
          <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-300">
              {/* Header */}
              <div className="p-6 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gray-100 rounded-lg">
                      <FaArrowUp className="text-gray-700 text-xl" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-gray-800">Room Upgrade Preview</h2>
                      <p className="text-sm text-gray-600">
                        Review upgrade details before confirming
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={closeModal}
                    className="text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-100 rounded"
                  >
                    <FaTimes size={20} />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                {loadingPreview ? (
                  <div className="h-64 flex justify-center items-center">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-900 mx-auto mb-4"></div>
                      <p className="text-gray-600">Loading upgrade details...</p>
                    </div>
                  </div>
                ) : previewData ? (
                  <div className="space-y-6">
                    {/* Room Comparison */}
                    <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
                      <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <FaBed className="text-gray-600" />
                        Room Comparison
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <div className="p-4 bg-white rounded-lg border border-gray-300">
                            <p className="text-sm text-gray-600 mb-2">Current Room</p>
                            <p className="font-bold text-gray-800 text-lg mb-1">
                              {currentRoomType}
                            </p>
                            <p className="text-sm text-gray-500 mb-3">
                              {currentRoomDetails?.description}
                            </p>
                            <div className="border-t border-gray-200 pt-3">
                              <p className="text-sm text-gray-600">Total Cost</p>
                              <p className="font-bold text-gray-800">
                                {formatCurrency(
                                  previewData.room_preview?.room_details?.old_room_final_total || 0
                                )}
                              </p>
                            </div>
                          </div>
                        </div>
                        <div>
                          <div className="p-4 bg-white rounded-lg border border-gray-900">
                            <p className="text-sm text-gray-600 mb-2">Upgrading To</p>
                            <p className="font-bold text-gray-900 text-lg mb-1">
                              {selectedRoom?.roomtypename}
                            </p>
                            <p className="text-sm text-gray-500 mb-3">
                              {selectedRoom?.description}
                            </p>
                            <div className="border-t border-gray-200 pt-3">
                              <p className="text-sm text-gray-600">Total Cost</p>
                              <p className="font-bold text-gray-900">
                                {formatCurrency(
                                  previewData.room_preview?.room_details?.new_room_final_total || 0
                                )}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Price Breakdown */}
                    <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                          <FaArrowUp className="text-gray-600" />
                          Upgrade Pricing Breakdown
                        </h3>
                        <button
                          onClick={() => setShowBreakdown(!showBreakdown)}
                          className="text-sm text-gray-700 hover:text-gray-900 font-medium px-3 py-1 hover:bg-gray-100 rounded"
                        >
                          {showBreakdown ? 'Hide Details' : 'Show Details'}
                        </button>
                      </div>

                      {showBreakdown && previewData.room_preview?.price_details && (
                        <div className="mb-4 bg-white rounded-lg border border-gray-300 p-4 space-y-3">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Additional Room Charges</span>
                            <span className="font-medium text-gray-800">
                              {formatCurrency(
                                previewData.room_preview.price_details.base_amount || 0
                              )}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Taxes & Fees</span>
                            <span className="font-medium text-gray-800">
                              {formatCurrency(
                                previewData.room_preview.price_details.tax_amount || 0
                              )}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Service Fee</span>
                            <span className="font-medium text-gray-800">
                              {formatCurrency(
                                previewData.room_preview.price_details.service_fee || 0
                              )}
                            </span>
                          </div>
                          <div className="border-t border-gray-300 pt-3">
                            <div className="flex justify-between font-bold text-gray-800">
                              <span>Total Upgrade Cost</span>
                              <span>
                                {formatCurrency(
                                  previewData.room_preview.price_details.final_amount || 0
                                )}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Summary */}
                      <div className="bg-gray-800 text-white rounded-lg p-5">
                        <h4 className="font-semibold text-white mb-4">Financial Summary</h4>
                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <span className="text-gray-300">Current Amount Due</span>
                            <span className="font-medium">
                              {formatCurrency(previewData.combined?.amount_due_after_preview || 0)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-300">Amount Paid</span>
                            <span className="font-medium text-white">
                              {' '}
                              {formatCurrency(previewData.combined?.amount_paid || 0)}
                            </span>
                          </div>
                          <div className="border-t border-gray-700 pt-3">
                            <div className="flex justify-between font-bold text-lg">
                              <span>Total Amount</span>
                              <span>
                                {formatCurrency(
                                  previewData.combined?.projected_total_after_preview || 0
                                )}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Current Room Details */}
                    <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
                      <h4 className="font-semibold text-gray-800 mb-3">Stay Details</h4>
                      <div className="grid grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-gray-600">Guest Name</p>
                          <p className="font-medium text-gray-800 whitespace-nowrap">{guestName}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Reservation Number</p>
                          <p className="font-medium text-gray-800">{orderId}</p>
                        </div>
                        <div>
                          <p className="text-gray-600">Check-in Date</p>
                          <p className="font-medium text-gray-800">
                            {formatDateToDisplay(checkinDate)}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-600">Check-out Date</p>
                          <p className="font-medium text-gray-800">
                            {formatDateToDisplay(checkoutDate)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="h-64 flex flex-col justify-center items-center text-center space-y-3">
                    <FaTimes className="text-red-500 text-3xl" />
                    <h2 className="text-lg font-semibold text-gray-800">
                      Failed to load upgrade details
                    </h2>
                    <p className="text-gray-600">Please try again or contact support</p>
                  </div>
                )}
              </div>

              {/* Footer Actions */}
              <div className="p-6 border-t border-gray-200 flex flex-col sm:flex-row justify-end gap-3">
                <button
                  onClick={closeModal}
                  className="px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpgradeConfirm}
                  className="px-6 py-3 bg-gray-900 text-white font-medium rounded-lg hover:bg-gray-800 transition-colors w-full"
                >
                  Upgrade Now
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Room Images Modal */}
        {showRoomImagesModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
            <div className="relative w-full max-w-4xl max-h-[80vh] bg-white rounded-xl shadow-xl flex flex-col">
              <div className="flex justify-between items-center p-4 border-b border-gray-200">
                <h1 className="text-lg font-semibold text-gray-800">Room Images</h1>
                <button
                  onClick={closeRoomImagesModal}
                  className="text-gray-500 hover:text-gray-700 transition"
                >
                  <FaTimes size={20} />
                </button>
              </div>

              <div className="relative flex-1 flex items-center justify-center p-4 overflow-hidden">
                <img
                  src={currentRoomImages[currentImageIndex]}
                  alt={`Room ${currentImageIndex + 1}`}
                  className="max-w-full max-h-[calc(80vh-120px)] object-contain"
                />

                {currentRoomImages.length > 1 && (
                  <>
                    <button
                      className="absolute left-2 md:left-4 top-1/2 transform -translate-y-1/2 bg-gray-900 bg-opacity-60 text-white p-3 rounded-full hover:bg-opacity-80 transition z-50"
                      onClick={handlePrevImage}
                    >
                      <FaChevronLeft size={20} />
                    </button>
                    <button
                      className="absolute right-2 md:right-4 top-1/2 transform -translate-y-1/2 bg-gray-900 bg-opacity-60 text-white p-3 rounded-full hover:bg-opacity-80 transition z-50"
                      onClick={handleNextImage}
                    >
                      <FaChevronRight size={20} />
                    </button>
                  </>
                )}
              </div>

              {currentRoomImages.length > 1 && (
                <div className="p-4 border-t border-gray-200">
                  <div className="flex justify-center space-x-2">
                    {currentRoomImages.map((_, index) => (
                      <button
                        key={index}
                        className={`w-2 h-2 md:w-3 md:h-3 rounded-full transition ${
                          currentImageIndex === index
                            ? 'bg-gray-900'
                            : 'bg-gray-300 hover:bg-gray-400'
                        }`}
                        onClick={() => setCurrentImageIndex(index)}
                        aria-label={`Go to image ${index + 1}`}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RoomUpgrade;
